import {md2html} from '@github-ui/md2html'
import {type ConnectionOperation, DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {IEnvironment} from 'relay-runtime'

import type {
  updateIssueBodyMutation,
  updateIssueBodyMutation$data,
  updateIssueBodyMutation$rawResponse,
  UpdateIssueInput,
} from './__generated__/updateIssueBodyMutation.graphql'

type commitUpdateIssueBodyMutationProps = {
  environment: IEnvironment
  input: {
    issueId: string
    body: string
    bodyVersion: string
  }
  onCompleted?: (response: updateIssueBodyMutation$data) => void
  onError?: (error: Error) => void
  withOptimisticResponse?: boolean
}

/** Every field the mutation selected other than `id` is what changed. */
function toIssueBodyOperation(
  data: updateIssueBodyMutation$data | updateIssueBodyMutation$rawResponse,
): ConnectionOperation | undefined {
  const issue = data.updateIssue?.issue
  if (!issue) return undefined
  const {id, ...fields} = issue
  return {type: 'setFields', id, fields}
}

export function commitUpdateIssueBodyMutation({
  environment,
  input: {issueId, body, bodyVersion},
  onCompleted,
  onError,
  withOptimisticResponse = false,
}: commitUpdateIssueBodyMutationProps) {
  const inputHash: UpdateIssueInput = {
    id: issueId,
    body,
    bodyVersion,
  }

  const optimisticResponse: updateIssueBodyMutation$rawResponse | undefined = withOptimisticResponse
    ? {
        updateIssue: {
          issue: {
            id: issueId,
            body,
            bodyVersion,
            bodyHTML: md2html(body),
            lastEditedAt: new Date().toISOString(),
            viewerCanReadUserContentEdits: true,
            lastUserContentEdit: null,
          },
        },
      }
    : undefined
  const mutationId = createConnectionMutationId()
  const optimisticOperation = optimisticResponse && toIssueBodyOperation(optimisticResponse)

  const dispatchOperationError = (error: Error) => dispatchConnectionMutation({phase: 'error', mutationId, error})

  // Published before `commitMutation`: a same-tick terminal would find nothing pending to settle.
  if (optimisticOperation) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId,
      ownerId: issueId,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation: optimisticOperation,
    })
  }

  let disposable
  try {
    disposable = commitMutation<updateIssueBodyMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation updateIssueBodyMutation($input: UpdateIssueInput!) @raw_response_type {
          updateIssue(input: $input) {
            issue {
              id
              bodyVersion
              body
              bodyHTML(unfurlReferences: true)
              viewerCanReadUserContentEdits
              lastEditedAt
              lastUserContentEdit {
                editor {
                  url
                  login
                }
              }
            }
          }
        }
      `,
      optimisticResponse,
      variables: {
        input: inputHash,
      },
      onCompleted: (response, errors) => {
        const operation = toIssueBodyOperation(response)
        if (!operation) {
          // The overlay rolls back, so reporting success would run the editor's success path over a
          // body that is simultaneously reverting.
          const error = new Error(errors?.[0]?.message ?? 'Failed to update issue body')
          dispatchOperationError(error)
          onError?.(error)
          return
        }
        dispatchConnectionMutation({
          phase: 'completed',
          mutationId,
          ownerId: issueId,
          connectionPath: DOCUMENT_FIELDS_PATH,
          operation,
        })
        onCompleted?.(response)
      },
      onError: error => {
        dispatchOperationError(error)
        if (onError) onError(error)
      },
      onUnsubscribe: () => {
        dispatchOperationError(new Error('Updating the issue body was cancelled'))
      },
    })
  } catch (error) {
    dispatchOperationError(error instanceof Error ? error : new Error('Failed to update issue body'))
    throw error
  }

  return disposable
}
